import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { voteSubmission } from '../api/client';
import { logger } from '../lib/logger';

const NODE_HEIGHT = 22;
const LEVEL_WIDTH = 200;
const CIRCLE_R = 5;

function slugify(text) {
  return (text || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

function assignIds(node, path = '0') {
  node._id = path;
  if (node.children) {
    node.children.forEach((c, i) => assignIds(c, `${path}.${i}`));
  }
  return node;
}

// Merge remote submissions into the static tree at their category/subcategory
function mergeSubmissions(root, submissions, onVoteChange) {
  if (!submissions?.length) return root;
  const cloned = JSON.parse(JSON.stringify(root));

  const findOrCreate = (parent, name) => {
    if (!parent.children) parent.children = [];
    let node = parent.children.find((c) => c.name === name);
    if (!node) {
      node = { name, children: [] };
      parent.children.push(node);
    }
    if (!node.children) node.children = [];
    return node;
  };

  for (const s of submissions) {
    const cat = cloned.children.find((c) => c.name === s.category);
    if (!cat) continue;
    let target = cat;
    if (s.subcategory) target = findOrCreate(cat, s.subcategory);
    if (!target.children) target.children = [];
    target.children.push({
      name: s.name,
      url: s.url,
      type: s.type,
      description: s.description,
      _submission: s,
    });
  }
  return cloned;
}

function computeLayout(root, expanded) {
  const nodes = [];
  const links = [];
  let yCounter = 1;

  function walk(node, depth) {
    const x = depth * LEVEL_WIDTH;
    const isExpanded = expanded.has(node._id);
    const hasChildren = node.children && node.children.length > 0;
    if (!isExpanded || !hasChildren) {
      const y = yCounter * NODE_HEIGHT;
      yCounter += 1;
      nodes.push({ node, x, y, depth });
      return y;
    }
    const childYs = node.children.map((c) => walk(c, depth + 1));
    const y = (childYs[0] + childYs[childYs.length - 1]) / 2;
    nodes.push({ node, x, y, depth });
    node.children.forEach((c) => {
      const childNode = nodes.find((n) => n.node === c);
      if (childNode) {
        links.push({
          from: { x, y },
          to: { x: childNode.x, y: childNode.y },
          id: `${node._id}->${c._id}`,
        });
      }
    });
    return y;
  }

  walk(root, 0);
  const maxDepth = Math.max(...nodes.map((n) => n.depth));
  const width = (maxDepth + 1) * LEVEL_WIDTH + 100;
  const height = yCounter * NODE_HEIGHT + 40;
  return { nodes, links, width, height };
}

function filterTree(root, query) {
  const q = query.trim().toLowerCase();
  if (!q) return { tree: root, expandIds: null };
  const expandIds = new Set();

  function clone(node) {
    const nameMatch = node.name.toLowerCase().includes(q);
    if (node.children && node.children.length) {
      const kids = node.children.map(clone).filter(Boolean);
      if (nameMatch || kids.length) {
        const n = { ...node, _id: node._id, children: kids.length ? kids : node.children };
        if (kids.length) expandIds.add(node._id);
        return n;
      }
      return null;
    }
    return nameMatch ? node : null;
  }
  const filtered = clone(root);
  return { tree: filtered || { ...root, children: [] }, expandIds };
}

const typeColor = {
  T: 'var(--chip-t)',
  R: 'var(--chip-r)',
  M: 'var(--chip-m)',
  D: 'var(--chip-d)',
};

export default function Tree({ data, query, submissions, onSubmissionsChange }) {
  const merged = useMemo(
    () => mergeSubmissions(data, submissions),
    [data, submissions]
  );
  const rootWithIds = useMemo(
    () => assignIds(JSON.parse(JSON.stringify(merged))),
    [merged]
  );

  const [expanded, setExpanded] = useState(() => new Set([rootWithIds._id]));

  const { tree: activeTree, expandIds } = useMemo(
    () => filterTree(rootWithIds, query || ''),
    [rootWithIds, query]
  );

  useEffect(() => {
    if (expandIds) {
      const newSet = new Set(expanded);
      newSet.add(rootWithIds._id);
      expandIds.forEach((id) => newSet.add(id));
      setExpanded(newSet);
    }
  }, [query]);

  const { nodes, links, width, height } = useMemo(
    () => computeLayout(activeTree, expanded),
    [activeTree, expanded]
  );

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleVote = async (sub, value) => {
    // If already voted the same way, clicking again removes vote (toggle)
    const nextValue = sub.user_vote === value ? 0 : value;
    try {
      const updated = await voteSubmission(sub.id, nextValue);
      onSubmissionsChange?.((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );
    } catch (e) {
      logger.error('Tree: submission vote failed', e);
    }
  };

  const pathFor = (from, to) => {
    const dx = (to.x - from.x) * 0.5;
    return `M ${from.x + CIRCLE_R} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x - CIRCLE_R} ${to.y}`;
  };

  return (
    <div className="tree-scroll">
      <div className="tree-canvas" style={{ width, height, minHeight: height }}>
        <svg
          className="tree-svg"
          width={width}
          height={height}
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        >
          {links.map((l) => (
            <path
              key={l.id}
              d={pathFor(l.from, l.to)}
              fill="none"
              stroke="var(--line)"
              strokeWidth="1"
            />
          ))}
        </svg>
        {nodes.map(({ node, x, y }) => {
          const hasChildren = node.children && node.children.length > 0;
          const isOpen = expanded.has(node._id);
          const isLeaf = !hasChildren && (node.url || node._route);
          const sub = node._submission;
          const label = (
            <span key={`label-${node._id}`} className={`node-label ${node.type ? `type-${node.type}` : ''}`}>
              {node.name}
              {node.type && (
                <span className="type-badge" style={{ color: typeColor[node.type] }}>
                  {' '}({node.type})
                </span>
              )}
              {sub && <span className="sub-star" title="Community-submitted"> ★</span>}
            </span>
          );
          return (
            <div
              key={node._id}
              className="node-wrap"
              style={{
                position: 'absolute',
                left: x - 300,
                top: y - 10,
                width: 300 + CIRCLE_R * 2,
                height: 20,
              }}
            >
              <div className="node-inner">
                {sub && (
                  <span className="vote-inline">
                    <button
                      type="button"
                      className={`vote-btn ${sub.user_vote === 1 ? 'active up' : ''}`}
                      onClick={() => handleVote(sub, 1)}
                      aria-label="Upvote"
                    >
                      <ChevronUp size={12} />
                    </button>
                    <span className={`vote-score ${sub.score > 0 ? 'pos' : sub.score < 0 ? 'neg' : ''}`}>
                      {sub.score}
                    </span>
                    <button
                      type="button"
                      className={`vote-btn ${sub.user_vote === -1 ? 'active down' : ''}`}
                      onClick={() => handleVote(sub, -1)}
                      aria-label="Downvote"
                    >
                      <ChevronDown size={12} />
                    </button>
                  </span>
                )}
                {isLeaf ? (
                  <Link
                    to={
                      node._route === 'creator'
                        ? `/creator/${node._slug || slugify(node.name)}`
                        : node._route === 'company'
                        ? `/company/${node._slug || slugify(node.name)}`
                        : node._route === 'discover'
                        ? `/discover/${node._slug || slugify(node.name)}`
                        : `/tool/${slugify(node.name)}`
                    }
                    className="leaf-link"
                    title={node.description || undefined}
                  >
                    {label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="branch-btn"
                    onClick={() => hasChildren && toggle(node._id)}
                  >
                    {label}
                  </button>
                )}
                <span
                  className={`node-circle ${hasChildren ? 'has-children' : 'is-leaf'} ${isOpen ? 'open' : ''}`}
                  onClick={() => hasChildren && toggle(node._id)}
                  role={hasChildren ? 'button' : undefined}
                  aria-label={hasChildren ? (isOpen ? 'collapse' : 'expand') : undefined}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
